/**
 * Fetches the list of severity/image pairs.
 *
 * @param {*} flawId
 * @param {*} rubricCode
 * @returns the list of severity/image pairs
 */
async function fetchImagesBySeverity(rubricCode, flawId, partnerUuid) {
  const query = `
    query imagesBySeverity(
      $rubricCode: String
      $problemAttributeId: Int
      $partnerUuid: String
    ) {
      imagesBySeverity(
        rubricCode: $rubricCode
        problemAttributeId: $problemAttributeId
        partnerUuid: $partnerUuid
      ) {
        id  
        name  
        imageUrl
      }
    }
  `;

  const param = 'query-fetchImagesBySeverity';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables: { rubricCode, problemAttributeId: flawId, partnerUuid } }),
    });

    const jsonData = await response.json();
    return jsonData.data.imagesBySeverity;
  } catch (e) {
    console.error(e);
    return null;
  }
}

/**
 * Fetches the recommended grade
 *
 * @param {Object} args
 * @param {args.parentSku} parentSku
 * @param {args.partnerUuid} partnerUuid
 * @param {args.itemIssues} itemIssues a list because the service expects a list
 * itemIssues has the following shape: {
 *  problemAttributeId,
 *  severityAttributeId,
 *  locationAttributeId,
 *  quantity,
 * } 
 * @returns the list of severity/image pairs
 */
 async function fetchRecommendedGrade({
   parentSku,
   partnerUuid,
   itemIssues,
   rubricCode,
 }) {
  const query = `
    query recommendedGrade(
      $parentSku: String
      $partnerUuid: String
      $itemIssues: [InventoryItemIssueInputTypeCommerce]
      $rubricCode: String

    ) {
      recommendedGrade(
        parentSku: $parentSku
        partnerUuid: $partnerUuid
        itemIssues: $itemIssues
        rubricCode: $rubricCode
      ) {
          code
          name
          displayName
          rank
      }
    }
  `;
  const param = 'query-fetchRecommendedGrade';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables: { parentSku, partnerUuid, itemIssues, rubricCode } }),
    });

    const jsonData = await response.json();
    return jsonData.data.recommendedGrade;
  } catch (e) {
    console.error(e);
    return null;
  }
}

if (document.querySelector('condition-grading-drawer')) {
  class ConditionGradingDrawer extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.querySelector('#ConditionGradingDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.flawId = null;
      this.onCloseCallback = null;
      this.chosenLocation = null;
      this.chosenSeverity = null;
      this.cleanup = () => {
        this.flawId = null;
        this.onCloseCallback = null;
        this.chosenLocation = null;
        this.chosenSeverity = null;
      };
      this.severityImageClickEvent = this.severityImageClick.bind(this);
      this.dropdownChangeEvent = this.dropdownChange.bind(this);
      this.saveButtonClickEvent = this.saveButtonClick.bind(this);
    }

    setupEditingForm() {
      const flawImages = this.querySelectorAll('.condition-grading-flaw-container');
      flawImages.forEach(el => {
        const isSelected = el.getAttribute('value') === JSON.stringify(this.chosenSeverity);
        if (isSelected) {
          el.classList.add('selected');
        } else {
          el.classList.remove('selected');
        }
      });
      const dropdown = this.querySelector('select');

      if (this.chosenLocation) {
        // Doing this because the option values do not have a flawId
        const dropdownValue = { ...this.chosenLocation };
        delete dropdownValue.flawId;
        dropdown.value = JSON.stringify(dropdownValue);
      } else {
        dropdown.value = '';
      }
    }

    checkForAssignedFlaw(flawId) {
      const locations = localStorage.getItem('locations');
      const severities = localStorage.getItem('severities');
      if (!locations || !severities)  return false;
      const foundLocation = JSON.parse(locations).find((location) => location.flawId === flawId);
      const foundSeverity = JSON.parse(severities).find((severity) => severity.flawId === flawId);
      if (!foundLocation || !foundSeverity) return false;
      return {
        foundLocation,
        foundSeverity,
      };
    }

    get severityImageContainers() {
      return this.querySelectorAll('.condition-grading-flaw-container');
    }

    get dropdown() {
      return this.querySelector('select');
    }

    get saveButton() {
      return this.querySelector('.condition-grading-save');
    }

    async severityImageClick(event) {
      this.severityImageContainers.forEach(el => {
        const isSelected = el === event.currentTarget;
        if (isSelected) {
          el.classList.add('selected');
        } else {
          el.classList.remove('selected');
        }
      });

      this.chosenSeverity = JSON.parse(event.currentTarget.getAttribute('value'));

      await this.handleSeverityAndLocationSelections();
    }

    async dropdownChange(event) {
      event.preventDefault();
      if (event.target.value) {
        this.chosenLocation = JSON.parse(event.target.value);
      } else {
        this.chosenLocation = null;
      }

      await this.handleSeverityAndLocationSelections();
    }

    async saveButtonClick(event) {
      event.preventDefault();
      this.save();
      await this.updateMainPageOnSave();
    }

    async handleSeverityAndLocationSelections(wasDialogJustOpened) {
      this.setSaveButtonDisabledState();
      await this._renderNoCreditDialogMessage(wasDialogJustOpened);
    }

    setSaveButtonDisabledState() {
      this.saveButton.disabled = !this.chosenSeverity || !this.chosenLocation;
    }

    async _renderNoCreditDialogMessage(wasDialogJustOpened = false) {
      const noCreditMessage = this.querySelector('.condition-grading__no-credit');
      if (wasDialogJustOpened) {
        // Default the "no credit" message to hidden to avoid showing a brief flash of a stale "no
        // credit" message from the LAST time the dialog was open.
        noCreditMessage.classList.add('hidden');
      }
      const severityForCheck = this.chosenSeverity;
      const locationForCheck = this.chosenLocation;
      const { tradeinValue } = this._computeEstimatedValue(
        await this._getRecommendedGradeForThisFlaw(),
      );
      // Guard against quick changes to the selected severity or location, as these could otherwise
      // cause us to render based on stale trade-in values.
      if (severityForCheck === this.chosenSeverity && locationForCheck === this.chosenLocation) {
        noCreditMessage.classList[tradeinValue === 0 ? 'remove' : 'add']('hidden');
      }
    }

    /**
     * Try to open the condition-grading drawer and render its contents.
     *
     * @param {args}
     * @param {args.rubricCode} - Pass in a rubricCode to condition-grade upon
     * @param {args.flawId} - Pass in a flawId to condition-grade upon
     * @param {args.flawLocations} - Pass in a list of flaw locations
     * @param {args.onClose} -- An optional callback that will be called when the user closes the
     * condition-grading drawer.
     */
    async tryOpen({
      rubricCode,
      flaw,
      flawLocations,
      onClose,
    }) {
      this.flawId = flaw.id;
      this.onCloseCallback = onClose;

      const foundFlaws = this.checkForAssignedFlaw(flaw.id);

      if (foundFlaws) {
        this.chosenLocation = foundFlaws.foundLocation;
        this.chosenSeverity = foundFlaws.foundSeverity;
      } else {
        this.chosenLocation = null;
        this.chosenSeverity = null;
      }

      const didRenderContents = await this.tryRenderContents({
        rubricCode,
        flaw,
        flawLocations,
      });
      if (!didRenderContents) {
        if (flaw.name.toLowerCase() === 'other') {
          await this.updateMainPageOnSave('GRADE_B');
        }
        return;
      }

      this.setupEditingForm();

      this.severityImageContainers.forEach(severityImage => {
        severityImage.addEventListener('click', this.severityImageClickEvent);
      });
      this.dropdown.addEventListener('change', this.dropdownChangeEvent);
      this.saveButton.addEventListener('click', this.saveButtonClickEvent);

      this.classList.remove('hidden');

      await this.handleSeverityAndLocationSelections(true);
    }

    async _getRecommendedGrade(locations, severities) {
      const itemIssues = locations.map((location, idx) => ({
        problemAttributeId: location.flawId,
        severityAttributeId: severities[idx].id,
        locationAttributeId: location.id,
        quantity: 1,
      }));

      const partnerUuid = localStorage.getItem('partnerUuid');
      const { parentSku, rubricCode } = currentTradeinItem;

      const recommendedGrade = await fetchRecommendedGrade({
        parentSku,
        partnerUuid,
        itemIssues,
        rubricCode,
      });

      return recommendedGrade?.code;
    }

    async _getRecommendedGradeForThisFlaw() {
      if (!this.chosenSeverity || !this.chosenLocation) return 'GRADE_A';
      return this._getRecommendedGrade(
        [
          {
            ...this.chosenLocation,
            flawId: this.flawId,
          },
        ],
        [this.chosenSeverity],
      );
    }

    async _getOverallRecommendedGrade() {
      const locations = JSON.parse(localStorage.getItem('locations'));
      const severities = JSON.parse(localStorage.getItem('severities'));
      return this._getRecommendedGrade(locations, severities);
    }

    _computeEstimatedValue(grade) {
      const conditionValues = currentTradeinItem.conditionValues || currentTradeinItem.submittedUnknownItemConditions;
      const { tradeinValue, displayTradeinValue } = conditionValues.find(
        conditionValue => conditionValue.condition.name === grade,
      ) || {
        tradeinValue: 0,
        displayTradeinValue: 0,
      };
      return { tradeinValue, displayTradeinValue };
    }

    _renderEstimatedValue({ tradeinValue, displayTradeinValue }) {
      const estimatedValueElement = document.getElementById('trade-in-condition-grading__estimated-value');
      const estimatedValueLabelElement = document.getElementById('trade-in-condition-grading__estimated-value-label');
      const estimatedPrepromoValueElement = document.getElementById('trade-in-condition-grading__estimated-prepromo-value');
      const promotionText = document.querySelector('.trade-in-condition-grading__promotion-text');
      if (tradeinValue !== displayTradeinValue) {
        estimatedPrepromoValueElement.textContent = formatMoneyForTradein(displayTradeinValue);
        estimatedPrepromoValueElement.classList.remove('hidden');
        promotionText.classList.remove('hidden');
        estimatedValueLabelElement.firstElementChild.style.setProperty('font-weight', '600', 'important');
      } else {
        estimatedPrepromoValueElement.classList.add('hidden');
        promotionText.classList.add('hidden');
        estimatedValueLabelElement.firstElementChild.style.removeProperty('font-weight');
      }
      estimatedValueElement.textContent = formatMoneyForTradein(tradeinValue);
    };

    _renderNoCreditMainPageMessage(isNoCredit) {
      const noCreditMessage = document.querySelector('.trade-in-condition-grading__no-credit');
      noCreditMessage.classList[isNoCredit ? 'remove' : 'add']('hidden');
    };

    async updateMainPageOnSave(passedInGrade) {
      const grade = passedInGrade ? passedInGrade : await this._getOverallRecommendedGrade();
      const { tradeinValue, displayTradeinValue } = this._computeEstimatedValue(grade);
      this._renderEstimatedValue({ tradeinValue, displayTradeinValue });
      this._renderNoCreditMainPageMessage(tradeinValue === 0);
      sessionStorage.setItem('currentTradeinItem', JSON.stringify({
        ...currentTradeinItem,
        recommendedGrade: grade,
        tradeinValue,
        displayTradeinValue,
      }));
    }

    _setDropdownValue(value) {
      document.getElementById('location').value = value;
    }

    save() {
      this._updateLocationsFromLocalStorage();
      this._updateSeveritiesFromLocalStorage();
      this.close();
    }

    close() {
      this.onCloseCallback?.();
      this.classList.add('hidden');
      this.severityImageContainers.forEach(severityImage => {
        severityImage.removeEventListener('click', this.severityImageClickEvent);
      });
      this.dropdown.removeEventListener('change', this.dropdownChangeEvent);
      this.saveButton.removeEventListener('click', this.saveButtonClickEvent);
      this.cleanup();
    }

    _updateLocationsFromLocalStorage() {
      const currentLocations = JSON.parse(localStorage.getItem('locations'))
        ?.filter((currentLocation) => currentLocation.flawId !== this.flawId)
        ?? [];

      const locationsUpdated = [
        ...currentLocations,
        { ...this.chosenLocation, flawId: this.flawId },
      ];

      localStorage.setItem('locations', JSON.stringify(locationsUpdated));
    }

    _updateSeveritiesFromLocalStorage() {
      const currentSeverities = JSON.parse(localStorage.getItem('severities'))
        ?.filter((currentSeverity) => currentSeverity.flawId !== this.flawId)
        ?? [];

      const severitiesUpdated = [
        ...currentSeverities,
        { ...this.chosenSeverity },
      ];

      localStorage.setItem('severities', JSON.stringify(severitiesUpdated));
    }

    async tryRenderContents({
      rubricCode,
      flaw,
      flawLocations,
    }) {
      this.querySelector('h2').textContent = flaw.name;
      const flawType = this.querySelector('.condition-grading-images-header');
      flawType.querySelector('div').textContent = `${flawType.querySelector('div').textContent} ${flaw.name}?`;

      const didRenderImages = await this.tryRenderSeverityImagePairs({
        rubricCode,
        flaw,
      });

      if (!didRenderImages) return false;

      this.renderLocationDropdown(flawLocations);

      return true;
    }

    async tryRenderSeverityImagePairs({
      rubricCode,
      flaw,
    }) {
      const partnerUuid = localStorage.getItem('partnerUuid');
      const imagesBySeverity = await fetchImagesBySeverity(rubricCode, flaw.id, partnerUuid);
      if (imagesBySeverity?.length > 0) {
        const flawContainer = this.querySelector('.condition-grading-flaw-container');
        const flawImageEls = imagesBySeverity.map((imageBySeverity, i) => {
          const thisFlawImageEl = i === 0 ? flawContainer : flawContainer.cloneNode(true);
          const selector = thisFlawImageEl.querySelector('img');
          thisFlawImageEl.setAttribute('id', `condition-grading-flaw-container-${imageBySeverity.id}`);
          thisFlawImageEl.setAttribute('tabindex', '0');
          selector.setAttribute('src', normalizeUrl(imageBySeverity.imageUrl, {width: '400', height: '400'}));
          selector.setAttribute('value', JSON.stringify({
            ...imageBySeverity,
            flawId: flaw.id,
          }));
          thisFlawImageEl.setAttribute('value', JSON.stringify({
            ...imageBySeverity,
            flawId: flaw.id,
          }));
          thisFlawImageEl.querySelector('.flaw-severity').textContent = imageBySeverity.name;
          return thisFlawImageEl;
        });
        flawContainer.parentNode.replaceChildren(...flawImageEls);
        return true;
      }
      return false;
    }

    renderLocationDropdown(flawLocations) {
      const flawLocationDropdown = this.querySelector('select');
      const flawLocationEls = flawLocations.map((location, i) => {
        const dropdownValue = document.createElement('option');
        dropdownValue.setAttribute('value', JSON.stringify(location));
        dropdownValue.textContent = location.name;
        return dropdownValue;
      });
      const defaultOption = document.createElement('option');
      const selectLocationMessage = TRADE_IN_CONDITION_GRADING_CONFIG.selectLocationMessage;

      defaultOption.setAttribute('value', '');
      defaultOption.selected = true;
      defaultOption.disabled = true;
      defaultOption.hidden = true;
      defaultOption.textContent = selectLocationMessage;
      flawLocationEls.unshift(defaultOption);
      flawLocationDropdown.replaceChildren(...flawLocationEls);
    }
  }
  customElements.define('condition-grading-drawer', ConditionGradingDrawer);
}
